export type TaxType =
  | 'accommodation_rate'
  | 'city_tax'
  | 'transfer_tax'
  | 'municipality_tax'
  | 'property_tax'
  | 'tourism_tax'
  | 'government_tax'
  | 'state_rate'
  | 'country_tax'
  | 'country_rate'
  | 'county_rate'
  | 'special_rate'
  | 'city_rate'
  | 'resort_fee'
  | 'environmental'
  | 'extra_person_fee'
  | 'laundry_fee'
  | 'cleaning_fee'
  | 'additional_fee'

export const TAX_TYPE_TO_TRANSLATION_KEY: Record<TaxType, string> = {
  accommodation_rate: 'tax-breakdown.accommodation-price',
  city_tax: 'tax-breakdown.city-tax',
  transfer_tax: 'tax-breakdown.transfer-tax',
  municipality_tax: 'tax-breakdown.municipality-tax',
  property_tax: 'tax-breakdown.property-tax',
  tourism_tax: 'tax-breakdown.tourism-tax',
  government_tax: 'tax-breakdown.government-tax',
  state_rate: 'tax-breakdown.state-tax',
  country_tax: 'tax-breakdown.country-tax',
  country_rate: 'tax-breakdown.country-rate',
  county_rate: 'tax-breakdown.county-rate',
  special_rate: 'tax-breakdown.special-fee',
  city_rate: 'tax-breakdown.city-fee',
  resort_fee: 'tax-breakdown.resort-fee',
  environmental: 'tax-breakdown.environmental-fee',
  extra_person_fee: 'tax-breakdown.extra-person-fee',
  laundry_fee: 'tax-breakdown.laundry-fee',
  cleaning_fee: 'tax-breakdown.cleaning-fee',
  additional_fee: 'global.additional-fees',
} as const

export const TAXES: readonly TaxType[] = [
  'accommodation_rate',
  'city_tax',
  'transfer_tax',
  'municipality_tax',
  'property_tax',
  'tourism_tax',
  'government_tax',
  'state_rate',
  'country_tax',
  'country_rate',
  'county_rate',
]

export const FEES: readonly TaxType[] = [
  'special_rate',
  'city_rate',
  'resort_fee',
  'environmental',
  'extra_person_fee',
  'laundry_fee',
  'cleaning_fee',
]

export interface TaxDetail {
  amount: number
  currency: string
  included: boolean
  name: string
  client?: {
    amount: number
    currency: string
  }
  local?: {
    amount: number
    currency: string
  }
}

export interface AdditionalFee {
  amount: number
  currency: string
  name?: string
  local?: {
    amount: number
    currency: string
  }
}

export interface RoomData {
  subtotal: number
  subtotalCurrency: string
  total: number
  totalCurrency: string
  totalWithFees?: number
  payDate?: string
  payNow?: number
  payLate?: number | null
  paymentPolicy?: string
  taxDetails?: TaxDetail[]
  additionalFees?: AdditionalFee[]
  insurance?: number | null
  insuranceCurrency?: string
  insuranceSelected?: boolean
  discount?: number
  discountAmount?: number
}

export interface AvailabilityData {
  availabilityId?: string
  sellingRate?: number
  bighead?: number
  payRate?: number
  taxes?: Array<{
    included: boolean
    amount: number
    currency: string
    name: string
  }>
  summary?: {
    bighead_rate?: { amount: number; currency: string }
    price_per_night?: { amount: number; currency: string }
    taxes?: { amount: number; currency: string }
    subtotal?: { amount: number; currency: string }
    total?: { amount: number; currency: string }
    additional_fees?: {
      amount: number
      currency: string
      local?: { amount: number; currency: string }
    }
    total_nightly?: { amount: number; currency: string }
    subtotal_nightly?: { amount: number; currency: string }
    total_with_fees?: { amount: number; currency: string }
    total_nightly_with_fees?: { amount: number; currency: string }
    subtotal_with_fees?: { amount: number; currency: string }
    subtotal_nightly_with_fees?: { amount: number; currency: string }
  }
}

export interface TotalData {
  insurance?: number | null
  insuranceCurrency?: string | null
  payLate?: number | null
  payNow?: number
  staywallet?: number | null
  staywalletCurrency?: string | null
  subtotal?: number | null
  subtotalNightlyWithFees?: number | null
  subtotalWithFees?: number | null
  taxes?: number | null
  total?: number | null
  totalCurrency?: string | null
  totalWithFees?: number | null
  totalWithFeesCurrency?: string | null
  additionalProducts?: Record<string, number> | null
  availabilities?: AvailabilityData[]
}

export interface WalletSummary {
  isVisible?: boolean
  availableAmount?: number
  appliedAmount?: number
  currency?: string
  selected?: boolean
  loading?: boolean
}

export interface AggregatedData {
  subtotal: number
  subtotalCurrency: string
  total: number
  totalCurrency: string
  totalWithFees: number
  payNow: number
  payLate: number
  roomsNumber: number
  isMultiRoom: boolean
  taxDetails: TaxDetail[]
  insurance: number
  insuranceCurrency?: string
  insuranceSelected: boolean
  hasInsurance: boolean
  insuranceAmount: number
  totalWithInsurance: number
  payNowWithInsurance: number
  paymentPolicy?: string
  payDate?: string
  paymentDisplayType: PaymentDisplayType
  paymentsByDate?: Array<{
    date: string
    amount: number
    currency: string
  }>
}

export const PAYMENT_POLICIES = {
  NON_REFUNDABLE: 'NRF',
  NO_RESERVATION: 'NOR',
  STAY_FOR_LONG: 'SFL',
} as const

export type PaymentPolicy = (typeof PAYMENT_POLICIES)[keyof typeof PAYMENT_POLICIES]

export const PAYMENT_DISPLAY_TYPES = {
  NON_REFUNDABLE: 'NON_REFUNDABLE',
  PAY_ON_ARRIVAL_ONLY: 'PAY_ON_ARRIVAL_ONLY',
  PAY_UPON_BOOKING_WITH_LATER_PAYMENT: 'PAY_UPON_BOOKING_WITH_LATER_PAYMENT',
  STAY_FOR_LONG_DEPOSIT: 'STAY_FOR_LONG_DEPOSIT',
  NONE: 'NONE',
} as const

export type PaymentDisplayType = (typeof PAYMENT_DISPLAY_TYPES)[keyof typeof PAYMENT_DISPLAY_TYPES]
